import React, { BaseSyntheticEvent, useEffect, useState } from 'react'
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonListHeader,
  IonLabel,
  IonList,
  IonItem,
  IonFooter,
  IonButtons,
  IonButton,
  IonToggle,
  IonSearchbar,
  IonSpinner,
} from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { IMultiSelectCustomInput, IOption, IOptions, IPNNVOption } from '../../../common/common.types'
import './MultiSelectModal.component.css'
import { useDispatch, useSelector } from 'react-redux'
import { initializeSearch, setSearchText } from '../../search/store/search.actions'
import { getSearchStateSelector } from '../../search/selectors'
import { getStyleForPHI, IPHIComponent } from '../../phi/phi.component'
import { isIOSDevice } from '../../../utilities/isiOS.util'
import SInput from '../../stringInput/StringInput.component'
import { OTHER_OPTION_TEXT_BOX_VALUE_ID } from '../MultiSelect.component'
import { deepCopy } from '../../../utilities/array.utils'
import { IAlertState } from '../../alert/store/alert.types'
import { showAlert } from '../../alert/store/alert.actions'

interface IMultiSelectModalProps extends IPHIComponent {
  listHeader?: string
  options: IOptions
  pnnvOptions: IOptions | null
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  savedValues: IOptions
  action: (...args: any[]) => any
  savedPNNVValue?: IPNNVOption | null
  pnnvAction?: (...args: any[]) => any
  callback?: (...args: any[]) => any
  dispatchData: any
  customInput?: IMultiSelectCustomInput
  showPNNVs: boolean
  nemsis: string
}

const MultiSelectModal: React.FC<IMultiSelectModalProps> = (props: IMultiSelectModalProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    listHeader,
    options,
    pnnvOptions = null,
    showModal,
    setShowModal,
    action,
    pnnvAction,
    callback,
    savedValues,
    savedPNNVValue = null,
    dispatchData,
    customInput,
    isPHI,
    showPNNVs,
    nemsis,
  } = props
  const search = useSelector(getSearchStateSelector)
  const { isLoading, filteredOptions, searchText } = search
  const isIOS = isIOSDevice()
  const [showTextBox, setshowTextBox] = useState(false)
  const [textBoxValue, setTextBoxValue] = useState('')
  const [selectedPnnvValue, setSelectedPnnvValue] = useState<IPNNVOption | null>(null)
  const [selectedValues, setSelectedValues] = useState(deepCopy(savedValues))

  useEffect(() => {
    setSelectedValues(deepCopy(savedValues))
    setSelectedPnnvValue(deepCopy(savedPNNVValue))
  }, [savedPNNVValue, showModal])

  useEffect(() => {
    const otherTextboxValueOption: IOption | undefined = selectedValues?.find(
      (value: IOption) => value.id === OTHER_OPTION_TEXT_BOX_VALUE_ID
    )
    const hasOtherOption = filteredOptions.findIndex((option) => option.id === customInput?.textboxEnabledId)
    if (isOtherOptionSelected() && hasOtherOption > 0) {
      setshowTextBox(true)
      if (otherTextboxValueOption) setTextBoxValue(otherTextboxValueOption.otherComments?.toString() || '')
    }
    if (hasOtherOption === -1) setshowTextBox(false)
  }, [filteredOptions, showModal])

  const hasOtherTextValidationFailed = () => {
    return customInput?.required && isOtherOptionSelected() && !textBoxValue
  }

  const handleSearch = (e: CustomEvent) => {
    dispatch(setSearchText(e.detail.value))
  }

  const resetReduxState = () => {
    dispatch(initializeSearch([]))
  }

  const cancel = () => {
    action(savedValues)
    if (pnnvAction) pnnvAction(savedPNNVValue)
    setTextBoxValue('')
    setSelectedPnnvValue(null)
    dismiss()
  }

  const dismiss = () => {
    resetReduxState()
    setShowModal(false)
  }

  const isOtherOptionSelected = () => {
    return selectedValues.findIndex((option: IOption) => option.id === customInput?.textboxEnabledId) !== -1
  }

  const isOtherOptionTexboxValueSaved = () => {
    return selectedValues.findIndex((option: IOption) => option.id === OTHER_OPTION_TEXT_BOX_VALUE_ID) !== -1
  }

  const save = () => {
    resetReduxState()
    setShowModal(false)
    const modifiedValues = deepCopy(selectedValues)
    if (customInput) {
      if (customInput.textboxEnabledId === OTHER_OPTION_TEXT_BOX_VALUE_ID) {
        modifiedValues.map((value: IOption) => {
          if (value.id === OTHER_OPTION_TEXT_BOX_VALUE_ID) value.otherComments = textBoxValue
          else value.otherComments = null
        })
      } else {
        if (isOtherOptionTexboxValueSaved()) {
          modifiedValues.splice(
            modifiedValues.findIndex((o: IOption) => o.id === OTHER_OPTION_TEXT_BOX_VALUE_ID),
            1
          )
        }
        modifiedValues.map((value: IOption) => {
          value.otherComments = null
        })
        if (isOtherOptionSelected() && textBoxValue) {
          modifiedValues.push({ id: OTHER_OPTION_TEXT_BOX_VALUE_ID, name: null, otherComments: textBoxValue } as any)
        }
      }
    }
    setSelectedValues(modifiedValues)
    dispatch(action(modifiedValues, dispatchData))
    if (pnnvAction) dispatch(pnnvAction(selectedPnnvValue, dispatchData))
    if (callback) {
      dispatch(callback())
    }
    setTextBoxValue('')
  }

  const handleToggle = (e: BaseSyntheticEvent, option: IOption) => {
    const isChecked = e.target.checked
    setSelectedValues((prev: IOptions) => {
      const alreadySelected = prev.some((o: IOption) => o.id === option.id)
      if (isChecked) {
        return alreadySelected ? prev : [...prev, option]
      }
      return alreadySelected ? prev.filter((o: IOption) => o.id !== option.id) : prev
    })

    if (isChecked) {
      if (selectedPnnvValue) {
        setSelectedPnnvValue(null)
      }

      if (customInput && customInput.textboxEnabledId === option.id) {
        setshowTextBox(true)
        setTimeout(() => {
          const content: HTMLIonContentElement | null = document.querySelector('#multiSelectList')
          content && content?.scrollBy(0, 70)
        }, 10)
      }
    } else if (customInput && customInput.textboxEnabledId === option.id) {
      setshowTextBox(false)
    }
  }

  const handleTextboxOnchange = (e: CustomEvent) => {
    setTextBoxValue(e.detail.value)
  }

  const showConfirmationBeforeClearingRegularValues = (e: BaseSyntheticEvent, option: IOption) => {
    const alertState: IAlertState = {
      header: listHeader ?? '',
      message: t('pnnv.userConfirmationMessage'),
      buttons: [
        {
          text: t('pnnv.cancel'),
          handler: () => {
            setTextBoxValue('')
            setSelectedPnnvValue(null)
          },
        },
        { text: t('pnnv.continueAndClear'), role: 'destructive', handler: () => selectOption(e, option) },
      ],
      showEqualWidthButton: true,
    }
    dispatch(showAlert(alertState))
  }

  const checkForConfirmation = (e: BaseSyntheticEvent, option: IOption) => {
    e.preventDefault()
    if (pnnvAction && e.target.checked && option.name && selectedValues && selectedValues.length > 0) {
      setPNNVValue(option)
      showConfirmationBeforeClearingRegularValues(e, option)
    } else selectOption(e, option)
  }

  const setPNNVValue = (option: IOption) => {
    const obj = {
      id: option.id,
      name: option.name,
      pn: '',
      nv: '',
    }
    const key = nemsis as keyof IOption
    if (option[key]) {
      if (option.isPN) {
        obj.pn = String(option[key])
      } else if (option.isNV) {
        obj.nv = String(option[key])
      }
    }
    setSelectedPnnvValue(obj as IPNNVOption)
  }
  const selectOption = (e: BaseSyntheticEvent, option: IOption) => {
    e.preventDefault()
    if (!pnnvAction) return
    if (e.target.checked) {
      if (option.name) {
        setPNNVValue(option)
        if (selectedValues && selectedValues.length > 0) setSelectedValues([]) // clearing the whole array for normal selectedValues
      }
    } else {
      setSelectedPnnvValue(null)
    }
    showTextBox && setshowTextBox(false)
  }

  return (
    <IonModal
      data-testid='MultiSelectModal'
      showBackdrop={showModal}
      isOpen={showModal}
      onDidDismiss={dismiss}
      className={isIOS ? 'multi-select-modal multi-select-modal-ios' : 'multi-select-modal'}>
      <IonHeader className='ion-no-border'>
        <IonToolbar color='light'>
          <IonListHeader color='light'>
            <IonLabel>{listHeader}</IonLabel>
          </IonListHeader>
        </IonToolbar>
        <IonToolbar color='light'>
          <IonSearchbar
            data-testid='Searchbar'
            debounce={0}
            value={searchText}
            onIonChange={handleSearch}
            autocomplete='off'></IonSearchbar>
        </IonToolbar>
      </IonHeader>
      <IonList id='multiSelectList'>
        {isLoading && <IonSpinner data-testid='Spinner' />}
        {options &&
          filteredOptions &&
          filteredOptions.map((option: IOption) => (
            <IonItem
              data-private={isPHI}
              style={getStyleForPHI(isPHI)}
              key={`Option_${option.id}`}
              data-skip-auto-enhance>
              <IonLabel>{option.name}</IonLabel>
              <IonToggle
                slot='start'
                name={option.name}
                value={`${option.id}`}
                checked={selectedValues.filter((o: IOption) => o.id === option.id).length > 0}
                onClick={(e) => handleToggle(e, option)}
              />
            </IonItem>
          ))}
        {customInput && showTextBox && (
          <div data-private={isPHI} style={getStyleForPHI(isPHI)}>
            <SInput
              maxlength={customInput.maxlength}
              label='Describe'
              color='primary'
              dataTestID={`Other-Input_${customInput?.textboxEnabledId}`}
              inputValue={textBoxValue}
              onChangeAction={(e) => handleTextboxOnchange(e)}
            />
            {hasOtherTextValidationFailed() && (
              <IonItem data-testid='OtherValidation' className='ion-no-margin validation'>
                <IonLabel color='danger' className='ion-text-wrap ion-no-margin ion-no-padding'>
                  {t('common.DescriptionIsRequired')}
                </IonLabel>
              </IonItem>
            )}
          </div>
        )}
        {searchText.length > 0 && !filteredOptions.length && (
          <IonItem key={'no_options_found'}>
            <IonLabel>
              {t('common.NoSearchResults')}
              {`"${searchText}"`}
            </IonLabel>
          </IonItem>
        )}
      </IonList>
      {showPNNVs && pnnvOptions && pnnvOptions.length > 0 && (
        <div className='pnnv-selection-section'>
          <hr className='picklist-divider' />
          <IonList lines='inset'>
            {pnnvOptions?.map((option: IOption) => (
              <IonItem
                key={option.id}
                data-testid={`Option_${option.id}`}
                className={`${selectedPnnvValue?.id === option.id ? 'selected-pnnv-option' : ''} pnnv-option`}
                data-skip-auto-enhance>
                <IonLabel id={`optionLabel_${option.id}`}>{option.name}</IonLabel>
                <IonToggle
                  slot='start'
                  name={option.name}
                  value={`${option.id}`}
                  disabled={!!selectedPnnvValue && selectedPnnvValue?.id !== option.id}
                  checked={selectedPnnvValue?.id === option.id}
                  onClick={(e) => checkForConfirmation(e, option)}
                />
              </IonItem>
            ))}
          </IonList>
        </div>
      )}
      <IonFooter className='ion-no-border'>
        <IonToolbar color='light' className='p-10'>
          <IonButtons>
            <IonButton
              data-testid='MultiSelectCancel'
              className='btn btn-secondary mr-8'
              expand='block'
              id='multiselect-modal-cancel-button'
              onClick={() => cancel()}>
              {t('common.buttons.Cancel')}
            </IonButton>
            <IonButton
              data-testid='MultiSelectSave'
              disabled={hasOtherTextValidationFailed()}
              className='btn btn-primary'
              expand='block'
              onClick={() => save()}
              id='multiselect-modal-save-button'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  )
}

export default MultiSelectModal
